/**
 * Numéro 2 du programme pédagogique ETM — La circulation
 *
 * Thème ETM officiel : Circulation routière (code thème C).
 * Couvre : priorités, intersections, dépassements, vitesse, distances de sécurité,
 * arrêt et stationnement.
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, BookOpen, Users } from 'lucide-react';

export const circulationEtmIssue = {
  // META
  slug: 'circulation',
  themeCode: 'C',
  issueNumber: 2,
  totalIssues: 9,
  category: 'La circulation routière',
  shortTitle: 'La circulation',
  title: 'Priorités, dépassements, intersections',
  kicker: 'Numéro 2 du programme pédagogique ETM',
  subtitle:
    "Toutes les règles qui régissent ta cohabitation avec les autres véhicules. Apprendre à lire une intersection comme un motard pro, anticiper les priorités et survivre aux dépassements.",
  heroImage: '/img/magazines/circulation.jpg',
  heroImageAlt: 'Conduite urbaine de nuit avec lumières de la ville en arrière-plan — illustration de la circulation',
  readTimeMin: 14,

  // SEO
  seoDescription:
    "Maîtriser la circulation routière pour l'ETM 2026 : priorité à droite, cédez-le-passage, stop, distances de sécurité, dépassement à moto. Lecture pédagogique + QCM final.",
  seoKeywords:
    'circulation moto ETM, priorité à droite, dépassement moto, distance de sécurité, intersection, code de la route 2026',

  // ÉDITO
  edito: {
    title: "Pourquoi 60 % des accidents moto ont lieu en intersection",
    body: "Six accidents mortels sur dix impliquant un motard surviennent à un croisement. Pas en ligne droite, pas dans un virage : à une intersection. Pourquoi ? Parce que <strong>l'œil humain a du mal à percevoir une moto</strong> qui arrive de côté — et parce que la majorité des motards ne savent pas <strong>imposer leur visibilité</strong>. Ce numéro te donne en 14 minutes les règles ET les réflexes qui font la différence entre un trajet anodin et un drame. À la fin, 10 questions QCM pour valider que tu as compris.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  // SECTIONS
  sections: [
    // === CHAPITRE 1 — Priorités ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Les 4 régimes de priorité en intersection',
      paragraphs: [
        "En France, toute intersection est régie par <strong>l'un des 4 régimes</strong> suivants — et c'est non négociable :",
        "<strong>1. La priorité à droite</strong> (régime par défaut). Si aucun panneau ni feu ne précise rien, c'est <strong>le véhicule venant de ta droite</strong> qui passe. Article R415-5 du Code de la route. Sanction si non-respect : 135 € + 4 points retirés.",
        "<strong>2. Le cédez-le-passage</strong> (panneau AB3a, triangle pointe en bas). Tu dois ralentir, et céder le passage à TOUT véhicule circulant sur la route prioritaire — qu'il vienne de gauche OU de droite. Tu ne dois t'engager que si tu peux le faire sans gêner.",
        "<strong>3. Le STOP</strong> (panneau AB4, octogone rouge). Marquage obligatoire : tu dois <strong>poser le pied au sol</strong> à la ligne d'effet du STOP — arrêt complet. Puis céder le passage. Sanction si non-respect : 135 € + 4 points + 3 ans de suspension possible. C'est la règle la plus contrôlée par les radars urbains.",
        "<strong>4. Le feu tricolore</strong>. Rouge fixe = arrêt absolu. Orange = arrêt sauf si tu ne peux plus t'arrêter sans danger. Vert = passage autorisé (mais reste vigilant aux retardataires). À noter : <strong>le feu rouge clignotant + croix de Saint-André</strong> = passage à niveau, jamais s'engager.",
      ],
      keyPoints: [
        "Par défaut : priorité à droite (R415-5)",
        "STOP = pied au sol obligatoire à la ligne",
        "Cédez-le-passage : ralentir + s'engager sans gêner",
        "Feu orange : on s'arrête si possible — pas une autorisation",
      ],
    },

    // === SAVIEZ-VOUS ? ===
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "Les intersections en <strong>« T »</strong> sont les plus dangereuses pour les motards. Pourquoi ? Parce que dans 80 % des cas, c'est une voiture qui sort d'une voie secondaire et qui <strong>ne perçoit pas la moto</strong> arrivant sur la voie principale. C'est le fameux phénomène <em>SMIDSY</em> (« Sorry, Mate, I Didn't See You »). Conséquence : à l'approche d'une intersection en T, un motard pro adopte la <strong>position de visibilité maximale</strong> (1/3 gauche de sa voie) et garde un doigt sur les freins.",
    },

    // === CHAPITRE 2 — Distances ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Distances de sécurité : la règle des 2 secondes',
      paragraphs: [
        "La distance de sécurité, ce n'est pas une question de mètres mais de <strong>temps</strong>. La règle officielle (R412-12) : tu dois pouvoir <strong>t'arrêter sans heurter le véhicule qui te précède</strong>, quoi qu'il fasse.",
        "<strong>La règle des 2 secondes</strong> (méthode ECSR). Repère un point fixe sur la route (panneau, marquage). Quand le véhicule devant le franchit, compte « 1001, 1002 ». Tu dois franchir ce point APRÈS 1002. Sinon : tu es trop près.",
        "<strong>Par temps de pluie ou nuit : double la distance</strong> — règle des 4 secondes. Et sur sol mouillé, la distance de freinage d'une moto peut doubler (passant de 25 m à 50 m à 90 km/h).",
        "<strong>Sur autoroute</strong>, la distance minimale légale = <strong>2 traits blancs</strong> entre toi et le véhicule devant (les bandes pointillées font 39 m, espacées de 13 m). Sanction si non-respect : 135 € + 3 points + suspension possible.",
      ],
      keyPoints: [
        "Règle des 2 secondes en temps sec",
        "Doubler à 4 secondes sous pluie / nuit / brouillard",
        "Autoroute : 2 traits blancs minimum entre véhicules",
        "Sanction : 135 € + 3 points",
      ],
    },

    // === PIÈGE ===
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Sur une moto, je freine plus vite qu'une voiture, donc je peux coller. » <strong>FAUX et dangereux.</strong> En cas de freinage d'urgence, une moto demande <strong>plus de distance qu'une voiture moderne équipée d'ABS et de répartiteur</strong>, surtout si tu n'as pas l'habitude du freinage couplé. À cela s'ajoute le risque de <strong>chute pendant le freinage</strong> — la voiture, elle, freine en glissant. Garde toujours tes 2 secondes, c'est la règle qui te sauve.",
    },

    // === CHAPITRE 3 — Dépassement ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: 'Le dépassement à moto : 7 étapes, pas une de moins',
      paragraphs: [
        "Le dépassement est le moment le plus accidentogène d'un trajet moto. Article R414-4 du Code de la route : tu dois <strong>t'assurer que la manœuvre est sans danger</strong> et tu dois utiliser <strong>tes clignotants</strong> dès l'intention.",
        "<strong>Les 7 étapes du dépassement pro</strong> (méthode ECSR) :",
        "<strong>1. Évaluer</strong> : distance à couvrir, visibilité, marquages au sol (ligne continue = interdit). <strong>2. Contrôle rétroviseur gauche + angle mort</strong> (rotation de tête !). <strong>3. Clignotant gauche</strong> activé au moins 3 secondes avant. <strong>4. Déboiter</strong> progressivement vers la gauche. <strong>5. Accélérer franchement</strong> pour réduire le temps passé à hauteur du véhicule. <strong>6. Contrôle rétroviseur droit + angle mort</strong> avant de se rabattre. <strong>7. Clignotant droit + retour sur ta voie</strong>, en laissant au moins 50 m de marge devant le véhicule dépassé.",
        "<strong>Cas particulier : la circulation inter-files (CIF)</strong> est <strong>tolérée</strong> en Île-de-France, Bouches-du-Rhône, Rhône, Gironde et Hérault depuis l'expérimentation 2025. Conditions : autoroute / 2x2 voies, embouteillage avéré, vitesse max 50 km/h, entre les 2 voies de gauche uniquement. Sanction hors zones autorisées : 135 € + 3 points.",
      ],
      keyPoints: [
        "Clignotant 3 secondes avant + angle mort vérifié",
        "Accélération franche pour réduire le temps de risque",
        "Marge de 50 m minimum avant de se rabattre",
        "CIF tolérée uniquement dans 5 départements en 2026",
      ],
    },

    // === CONSEIL DU MONITEUR ===
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Le dépassement, ce n'est pas une histoire de cylindrée mais de <strong>visibilité</strong>. Si je vois mon élève hésiter, je lui dis : avant de dépasser, vérifie 3 choses dans cet ordre : (1) que tu peux voir <strong>la plaque arrière</strong> du véhicule qui te précède dans ton rétro = tu es à bonne distance, (2) que le marquage au sol l'autorise (ligne discontinue), (3) que tu as au moins <strong>200 mètres</strong> de visibilité devant. Si l'un des 3 manque, tu attends. La patience n'a jamais tué personne sur deux roues. »",
    },

    // === CHAPITRE 4 — Vitesse ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 4',
      title: "Vitesses légales 2026 : ce que tu dois connaître par cœur",
      paragraphs: [
        "Les limitations dépendent du type de voie ET des conditions météo. Pour une moto homologuée route, voici le tableau officiel 2026 :",
        "<strong>Agglomération</strong> : 50 km/h (parfois 30 km/h en zone apaisée). <strong>Hors agglomération</strong> : 80 km/h sur route bidirectionnelle sans séparateur central. <strong>Route à 2x2 voies avec séparateur</strong> : 110 km/h. <strong>Autoroute</strong> : 130 km/h (110 par temps de pluie).",
        "<strong>Cas du jeune permis (permis probatoire)</strong> : pendant les 3 ans suivant l'obtention (2 ans pour les passages en AAC), les limitations sont réduites de 10 à 20 km/h : 80 → 80 (inchangé), 110 → 100, 130 → 110. Disque « A » obligatoire à l'arrière.",
        "<strong>Excès de vitesse moto</strong> : sanctions de 68 € + 1 point (excès < 20 km/h) à 1 500 € + 6 points + suspension (excès > 50 km/h, considéré comme grand excès). À noter : un radar peut flasher une moto par l'arrière dans les zones urbaines depuis 2023.",
      ],
      keyPoints: [
        "50 / 80 / 110 / 130 = à connaître par cœur",
        "Jeune permis : -10 km/h sur 110 et 130",
        "Pluie : 110 max sur autoroute",
        "Disque A obligatoire 3 ans (2 ans en AAC)",
      ],
    },

    // === TÉMOIGNAGE ===
    {
      type: 'story',
      icon: Users,
      title: "Le témoignage : « Une intersection a changé ma vie »",
      body: "« Je rentrais de la fac, croisement classique en ville. Une Clio sortant d'une rue à droite a forcé sa priorité — sauf qu'elle ne m'avait simplement pas vu. J'ai freiné des deux roues, la moto a glissé, je suis passé sur le capot et j'ai atterri 4 mètres plus loin. Bilan : clavicule cassée, poignet fracturé, 6 semaines d'arrêt. La conductrice m'a dit en pleurant : « Mais Monsieur, je vous le jure, je ne vous ai pas vu ». Aujourd'hui, à chaque intersection, je ralentis ET je me décale à gauche dans ma voie pour me rendre visible. Une intersection, ça se prépare 100 m avant. » <em>— Karim, 26 ans</em>",
    },
  ],

  // CHIFFRE MARQUANT
  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '60 %',
    middle: 'des accidents mortels',
    label: 'à moto surviennent à une intersection',
    source: 'ONISR — Bilan accidentologie 2024',
    accent: '#7c3aed',
  },

  // GLOSSAIRE
  glossary: [
    { term: 'Priorité à droite', def: "Régime par défaut en intersection sans signalisation. Le véhicule venant de la droite passe en premier (art. R415-5)." },
    { term: 'Cédez-le-passage', def: "Panneau AB3a (triangle pointe en bas). Obligation de ralentir et de céder le passage à tout véhicule sur la route prioritaire." },
    { term: 'STOP', def: "Panneau AB4 (octogone rouge). Arrêt complet obligatoire avec pied au sol à la ligne d'effet. Puis céder le passage." },
    { term: 'Ligne d\'effet', def: "Marquage blanc transversal au sol qui matérialise le point où le véhicule doit s'arrêter à un STOP ou à un feu rouge." },
    { term: 'Règle des 2 secondes', def: "Méthode pour évaluer la distance de sécurité : compter 2 secondes entre le passage d'un point fixe par le véhicule devant et toi." },
    { term: 'Angle mort', def: "Zone non couverte par les rétroviseurs. Pour la corriger, rotation de la tête obligatoire avant tout changement de voie." },
    { term: 'CIF', def: "Circulation Inter-Files. Tolérée depuis 2025 dans 5 départements français (IDF, Bouches-du-Rhône, Rhône, Gironde, Hérault) sous conditions strictes." },
    { term: 'SMIDSY', def: "« Sorry Mate, I Didn't See You » — phénomène psycho-perceptif où l'œil d'un automobiliste ne détecte pas une moto qui arrive de côté." },
    { term: 'Permis probatoire', def: "Période de 3 ans (ou 2 en AAC) après obtention du permis, avec capital de 6 points et limitations de vitesse réduites." },
    { term: 'Grand excès de vitesse', def: "Excès de vitesse supérieur à 50 km/h au-dessus de la limite. Délit. Sanction : suspension immédiate + 1 500 € + 6 points." },
  ],

  // QCM
  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur la circulation routière tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong> pour valider ta maîtrise du thème.",
    themeCode: 'C',
    questionsCount: 10,
    passingScore: 8,
  },

  // NEXT
  nextIssue: {
    slug: 'autres-usagers',
    number: 4,
    title: 'Vivre avec piétons, vélos, camions',
    short: 'Les autres usagers de la route',
  },
};

export default circulationEtmIssue;
